import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, formatErr } from "@/lib/api";

const AuthCtx = createContext(null);
export const useAuth = () => useContext(AuthCtx);

const USER_KEY = "bq_user";

const readCachedUser = () => {
  try {
    const raw = localStorage.getItem(USER_KEY);
    return raw ? JSON.parse(raw) : undefined;
  } catch { return undefined; }
};

export const AuthProvider = ({ children }) => {
  // Restore the last authenticated user synchronously so CMS navigation does not
  // briefly redirect to /login while /auth/me is validating the saved JWT.
  const [user, setUserState] = useState(readCachedUser);
  const setUser = (value) => {
    setUserState(value);
    try {
      if (value) localStorage.setItem(USER_KEY, JSON.stringify(value));
      else localStorage.removeItem(USER_KEY);
    } catch {}
  };
  const [error, setError] = useState("");

  const refresh = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
    } catch (e) {
      // Keep a cached user while a transient request fails. Clear authentication only
      // when the server explicitly says the saved token is no longer valid.
      if (e?.response?.status === 401) {
        localStorage.removeItem("bq_token");
        setUser(null);
      }
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const persistToken = (t) => { if (t) localStorage.setItem("bq_token", t); };

  const login = async (email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      persistToken(data.token);
      setUser(data.user);
      return true;
    } catch (e) { setError(formatErr(e)); return false; }
  };

  const register = async (name, email, password) => {
    setError("");
    try {
      const { data } = await api.post("/auth/register", { name, email, password });
      persistToken(data.token);
      setUser(data.user);
      return true;
    } catch (e) { setError(formatErr(e)); return false; }
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("bq_token");
    localStorage.removeItem(USER_KEY);
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, error, login, register, logout, refresh, setUser }}>
      {children}
    </AuthCtx.Provider>
  );
};
